import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { faBuilding, faCircleCheck, faPercent } from "@fortawesome/free-solid-svg-icons"
import { useGetResellerClients, ResellerClient } from "../../../hooks/Reseller/useGetResellerClients"
import { EResellerCommissionDurationType } from "../../../Enum/EResellerCommissionDurationType"
import { formatDateToString } from "../../../Objects/Generics/Helps"
import { FinancialDataTable } from "../../CashFlow/components/FinancialDataTable"
import { FinancialKpiCard } from "../../CashFlow/components/FinancialKpiCard"
import type { FinancialTableColumn } from "../../CashFlow/types/financialUi"

export const ResellerClientsSection: React.FC = () => {
  const { t } = useTranslation("reseller")

  const { handleGetResellerClients } = useGetResellerClients()

  const [clients, setClients] = useState<ResellerClient[] | null>(null)

  useEffect(() => {
    handleGetResellerClients(setClients)
  }, [])

  const rows = clients ?? []

  const columns = useMemo<FinancialTableColumn<ResellerClient>[]>(
    () => [
      {
        id: "company",
        label: t("clients.column_company"),
        render: (client) => <span className="font-extrabold text-slate-800">{client.companyName || "—"}</span>,
        sortValue: (client) => client.companyName,
        mobilePriority: "primary",
      },
      {
        id: "status",
        label: t("clients.column_status"),
        render: (client) => client.subscriptionStatus,
        sortValue: (client) => client.subscriptionStatus,
        mobilePriority: "summary",
      },
      {
        id: "since",
        label: t("clients.column_since"),
        render: (client) => formatDateToString(client.linkedAtUtc),
        sortValue: (client) => new Date(client.linkedAtUtc).getTime(),
      },
      {
        id: "commission",
        label: t("clients.column_commission"),
        render: (client) => (
          <span className="font-bold text-orange-600">
            {client.commissionPercentageSnapshot}%{" "}
            {client.commissionDurationTypeSnapshot === EResellerCommissionDurationType.Recurring
              ? t("clients.duration_recurring")
              : t("clients.duration_until", {
                  date: client.commissionEndsAtUtc ? formatDateToString(client.commissionEndsAtUtc) : "—",
                })}
          </span>
        ),
        sortValue: (client) => client.commissionPercentageSnapshot,
        mobilePriority: "summary",
      },
      {
        id: "commissionStatus",
        label: t("clients.column_commission_status"),
        render: (client) => (
          <span
            className={`rounded-full px-3 py-1 text-xs font-bold ${
              client.isCommissionActive ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
            }`}
          >
            {client.isCommissionActive ? t("clients.commission_active") : t("clients.commission_ended")}
          </span>
        ),
        sortValue: (client) => Number(client.isCommissionActive),
      },
    ],
    [t],
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <FinancialKpiCard title={t("clients.title")} value={`${rows.length}`} icon={faBuilding} tone="brand" />

        <FinancialKpiCard
          title={t("clients.commission_active")}
          value={`${rows.filter((client) => client.isCommissionActive).length}`}
          icon={faCircleCheck}
          tone="positive"
        />

        <FinancialKpiCard
          title={t("clients.column_commission")}
          value={`${rows.length > 0 ? Math.max(...rows.map((client) => client.commissionPercentageSnapshot)) : 0}%`}
          icon={faPercent}
          tone="violet"
        />
      </div>

      <FinancialDataTable
        title={t("clients.title")}
        rows={rows}
        getRowKey={(client) => client.id}
        columns={columns}
        searchText={(client) => `${client.companyName} ${client.subscriptionStatus}`}
        searchLabel={t("clients.search")}
        emptyTitle={t("clients.empty")}
        icon={faBuilding}
        paginationLabels={{
          ariaLabel: t("clients.title"),
          previous: t("table.previous"),
          next: t("table.next"),
          page: (current, total) => t("table.page", { current, total }),
          range: (first, last, total) => t("table.range", { first, last, total }),
          itemsPerPage: t("table.items_per_page"),
        }}
      />
    </div>
  )
}
